import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';
import LanguageSelector from '../components/LanguageSelector';
import { FloatingInstallShare } from '../components/FloatingInstallShare';
import {
  ArrowRight, Shield, Users, Calendar, MessageSquare,
  BarChart3, Clock, CheckCircle2, ChevronRight,
  Briefcase, Building2, UserPlus, Ticket, LogIn, Download, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

const features = [
  { icon: Users, title: 'Staff Management', description: 'Manage caregivers, track certifications, and handle scheduling all in one place.' },
  { icon: Calendar, title: 'Smart Scheduling', description: 'Automated shift management with conflict detection and caregiver matching.' },
  { icon: MessageSquare, title: 'Secure Messaging', description: 'HIPAA-compliant communication between staff, clients, and families.' },
  { icon: Shield, title: 'Care Documentation', description: 'Digital care plans, incident reports, and progress tracking.' },
  { icon: BarChart3, title: 'Analytics & Reports', description: 'Real-time insights into operations, performance, and compliance.' },
  { icon: Clock, title: 'Time & Attendance', description: 'GPS-verified clock-in/out with automated timesheet generation.' }
];

const benefits = [
  'Reduce administrative overhead by 60%',
  'Improve caregiver retention rates',
  'Ensure regulatory compliance',
  'Enhance family engagement',
  'Real-time visibility into operations'
];

export function Landing() {
  const { isAuthenticated, user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') toast.success('App installed!');
      setDeferredPrompt(null);
    } else {
      toast.info('To install: use your browser menu → "Add to Home Screen"');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'FendanaCura', text: 'Smart care, seamlessly managed', url: window.location.origin });
      } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(window.location.origin);
      toast.success('Link copied to clipboard!');
    }
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    const paths = {
      agency_admin: '/dashboard/admin', ceo: '/dashboard/ceo', caregiver: '/dashboard/caregiver',
      client: '/dashboard/client', family: '/dashboard/family', developer: '/dashboard/developer',
      potential_client: '/browse', job_seeker: '/jobs'
    };
    return paths[user.role] || '/dashboard';
  };

  return (
    <div className="min-h-screen bg-background relative">
      <div className="glass-blobs"><div className="blob-3" /></div>
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 sm:h-10 w-auto" />
              <span className="text-lg sm:text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-browse-agencies">Browse Agencies</Link>
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-browse-jobs">Browse Jobs</Link>
              <Link to="/register-company" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-register-agency">Register Agency</Link>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>
              <ThemeToggle />
              {isAuthenticated ? (
                <Link to={getDashboardPath()}>
                  <Button className="btn-primary px-3 sm:px-6 text-xs sm:text-sm" data-testid="go-to-dashboard-btn"><span className="hidden sm:inline">Dashboard</span><ArrowRight className="w-4 h-4 sm:ml-2" /></Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button className="btn-primary px-3 sm:px-6 text-xs sm:text-sm" data-testid="sign-in-nav-btn"><LogIn className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Sign In</span></Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00A6A0]/5 to-transparent dark:from-[#00A6A0]/10" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="welcome-animation">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00A6A0]/10 border border-[#00A6A0]/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#34D399] animate-pulse" />
                <span className="text-sm text-[#00A6A0]">Trusted by 500+ care agencies</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground tracking-tight leading-none mb-6">
                Smart care,{' '}
                <span className="text-gradient-teal">seamlessly managed</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                FendanaCura is the all-in-one platform for home care agencies.
                Streamline operations, improve care quality, and grow your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register-company">
                  <Button className="btn-primary px-8 py-6 text-lg" data-testid="hero-register-btn">
                    Register Your Agency<ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/browse">
                  <Button variant="outline" className="btn-secondary px-8 py-6 text-lg" data-testid="hero-browse-btn">
                    Browse Agencies
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-8">
                <div>
                  <p className="text-3xl font-heading font-bold text-[#00A6A0]">98%</p>
                  <p className="text-sm text-muted-foreground">Client satisfaction</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div>
                  <p className="text-3xl font-heading font-bold text-[#F97316]">50K+</p>
                  <p className="text-sm text-muted-foreground">Shifts managed</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div>
                  <p className="text-3xl font-heading font-bold text-[#4A90E2]">24/7</p>
                  <p className="text-sm text-muted-foreground">Support available</p>
                </div>
              </div>
            </div>
            {/* Logo panel instead of placeholder image */}
            <div className="relative hidden lg:flex items-center justify-center">
              <div className="w-80 h-80 rounded-3xl glass-card flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00A6A0]/10 to-[#4A90E2]/10" />
                <img src={LOGO_URL} alt="FendanaCura" className="w-48 h-48 object-contain relative z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== PUBLIC NAVIGATION HUB ====== */}
      <section className="py-16 px-4" data-testid="public-nav-hub">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-3">
              What would you like to do?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Whether you're a care seeker, job seeker, or agency — FendanaCura has you covered
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5 max-w-3xl mx-auto mb-8">
            {/* Browse Jobs */}
            <Link to="/jobs" className="group" data-testid="hub-browse-jobs">
              <div className="glass-card glass-card-hover p-6 h-full">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-[#00A6A0]/10">
                  <Briefcase className="w-6 h-6 text-[#00A6A0]" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Browse Jobs</h3>
                <p className="text-sm text-muted-foreground">Explore caregiver positions — full time, part time, contract, and more</p>
              </div>
            </Link>

            {/* Sign Up (individual) */}
            <Link to="/signup" className="group" data-testid="hub-sign-up">
              <div className="glass-card glass-card-hover p-6 h-full">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-[#FF6F61]/10">
                  <UserPlus className="w-6 h-6 text-[#FF6F61]" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Sign Up</h3>
                <p className="text-sm text-muted-foreground">Create an account as a job seeker, care seeker, or family member</p>
              </div>
            </Link>

            {/* Invitation Token */}
            <Link to="/invite" className="group" data-testid="hub-invitation-token">
              <div className="glass-card glass-card-hover p-6 h-full">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-[#A8E6CF]/10">
                  <Ticket className="w-6 h-6 text-[#A8E6CF]" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Have an Invitation?</h3>
                <p className="text-sm text-muted-foreground">Use your invitation token to join an existing agency</p>
              </div>
            </Link>

            {/* Already have an account */}
            <Link to="/login" className="group" data-testid="hub-sign-in">
              <div className="glass-card glass-card-hover p-6 h-full">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-[#4A90E2]/10">
                  <LogIn className="w-6 h-6 text-[#4A90E2]" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Already Have an Account?</h3>
                <p className="text-sm text-muted-foreground">Sign in to access your dashboard</p>
              </div>
            </Link>
          </div>

          {/* Floating Install & Share glow buttons */}
          <div className="flex justify-center gap-6 mt-8" data-testid="install-share-section">
            <button
              onClick={handleInstall}
              className="group relative w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
              data-testid="landing-install-btn"
            >
              <div className="absolute inset-0 rounded-full bg-[#00A6A0]/20 animate-pulse" />
              <div className="absolute inset-1 rounded-full bg-[#00A6A0]/10 group-hover:bg-[#00A6A0]/20 backdrop-blur-sm border border-[#00A6A0]/30 shadow-[0_0_20px_rgba(0,166,160,0.3)] group-hover:shadow-[0_0_30px_rgba(0,166,160,0.5)] transition-all" />
              <Download className="w-6 h-6 text-[#00A6A0] relative z-10" />
              <span className="text-[10px] font-medium text-[#00A6A0] relative z-10 mt-0.5">Install</span>
            </button>
            <button
              onClick={handleShare}
              className="group relative w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
              data-testid="landing-share-btn"
            >
              <div className="absolute inset-0 rounded-full bg-[#4A90E2]/20 animate-pulse" style={{ animationDelay: '0.5s' }} />
              <div className="absolute inset-1 rounded-full bg-[#4A90E2]/10 group-hover:bg-[#4A90E2]/20 backdrop-blur-sm border border-[#4A90E2]/30 shadow-[0_0_20px_rgba(74,144,226,0.3)] group-hover:shadow-[0_0_30px_rgba(74,144,226,0.5)] transition-all" />
              <Share2 className="w-6 h-6 text-[#4A90E2] relative z-10" />
              <span className="text-[10px] font-medium text-[#4A90E2] relative z-10 mt-0.5">Share</span>
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Everything you need to run your agency
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive tools designed specifically for home care operations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const colors = ['#00A6A0', '#F97316', '#4A90E2', '#34D399', '#00A6A0', '#F97316'];
              const color = colors[index % colors.length];
              return (
                <div key={index} className="p-6 rounded-xl glass-card glass-card-hover" data-testid={`feature-card-${index}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-6 h-6" style={{ color }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex items-center justify-center">
              <div className="w-72 h-72 rounded-3xl glass-card flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6F61]/10 to-[#A8E6CF]/10" />
                <img src={LOGO_URL} alt="FendanaCura" className="w-40 h-40 object-contain relative z-10 opacity-80" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
                Why agencies choose FendanaCura
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#34D399] mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register-company" className="inline-block mt-8">
                <Button className="btn-primary px-8" data-testid="benefits-cta-btn">
                  Start Free Trial<ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00A6A0]/5 to-[#4A90E2]/5 dark:from-[#00A6A0]/10 dark:to-[#4A90E2]/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Ready to transform your care agency?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join hundreds of agencies already using FendanaCura to deliver better care
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register-company">
              <Button className="btn-primary px-8 py-6 text-lg" data-testid="cta-register-btn">
                Register Your Agency<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/jobs">
              <Button variant="outline" className="btn-secondary px-8 py-6 text-lg" data-testid="cta-browse-jobs-btn">
                <Briefcase className="w-5 h-5 mr-2" />Browse Jobs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
              <span className="text-lg font-heading font-bold text-foreground">FendanaCura</span>
            </div>
            <p className="text-muted-foreground text-sm">
              &copy; {new Date().getFullYear()} FendanaCura. Smart care, seamlessly managed.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/browse" className="text-muted-foreground hover:text-foreground text-sm">Browse Agencies</Link>
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground text-sm">Find Jobs</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground text-sm">Privacy</Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground text-sm">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
      <FloatingInstallShare />
    </div>
  );
}

export default Landing;
